import { Component, OnInit } from "@angular/core";
import { HrserviceService } from "../../hr-manager/service/hrservice.service";
import { ActivatedRoute } from "@angular/router";
import { Router } from "@angular/router";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Notification, Subject } from "rxjs";
import { EmponboardingService } from "../../employee-onboarding/services/emponboarding.service";
import { LoginService } from "src/app/core/login/login.service";
import { LoaderService } from "src/app/core/services/loader.service";
import { MatDialog } from "@angular/material/dialog";
import { FilterDialogComponent, FilterDialogData } from '../shared/filter-dialog/filter-dialog.component';
import { AddItemDialogComponent } from '../shared/add-item-dialog/add-item-dialog.component';
import { AddDialogConfigs } from '../shared/add-item-dialog/add-dialog-configs';
import { DepartmentDialogComponent } from '../department-dialog/department-dialog.component';
import { ExcelService } from "src/app/core/services/excel.service";

@Component({
    selector: "app-department-list",
    templateUrl: "./department-list.component.html",
    styleUrls: ["./department-list.component.scss", "../shared/hr-table-styles.scss"],
    providers: [ExcelService]
})

export class DepartmentListComponent implements OnInit {
    headingDepartment: string = "Departments";
    departmentlist: any[] = [];
    businessList: any[] = [];
    searchQuery: string = '';
    
    // Pagination
    currentPage: number = 1;
    totalPages: number = 1;
    pageSize: number = 10;
    totalCount: number = 0;
    
    // Filter state
    currentFilterState: any = {
        page: 1,
        name: '',
        business_id: '',
        department_id: ''
    };
    
    private searchSubject = new Subject<string>();
    
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private hrservice: HrserviceService,
        private EmpOnboardingService: EmponboardingService,
        private loginService: LoginService,
        private loaderService: LoaderService,
        private dialog: MatDialog,
        private excelService: ExcelService
    ) {
        // Setup search with debounce
        this.searchSubject
            .pipe(debounceTime(1000), distinctUntilChanged())
            .subscribe((query) => {
                this.currentFilterState.name = query;
                this.currentFilterState.page = 1;
                this.currentPage = 1;
                this.loadDepartmentList();
            });
    }

    ngOnInit(): void {
        this.loadDepartmentList();
    }

    // Load department list with current filters
    loadDepartmentList(): void {
        this.loaderService.setLoading(true);
        this.currentFilterState.page = this.currentPage;
        
        console.log('Department API params:', this.currentFilterState);
        
        this.hrservice.getDepartmentList(this.currentFilterState).subscribe(
            (res: any) => {
                this.departmentlist = res.data || [];
                this.totalCount = res.total_count || 0;
                this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                this.loaderService.setLoading(false);
            },
            (error) => {
                console.error('Error loading department list:', error);
                this.loaderService.setLoading(false);
                this.departmentlist = [];
            }
        );
    }

    // Handle search input
    onSearchInput(query: string): void {
        this.searchQuery = query;
        this.searchSubject.next(query);
    }

    // Open filter dialog
    openFilterDialog(): void {
        // Fetch business/department data using business_dep_list API before opening dialog
        this.hrservice.getDepartments().subscribe((res: any) => {
            this.businessList = res.data || [];
            
            const filterData: FilterDialogData = {
                title: 'Filter Departments',
                filters: [
                    {
                        key: 'name',
                        label: 'Department Name',
                        type: 'text',
                        placeholder: 'Enter department name'
                    },
                    {
                        key: 'business_id',
                        label: 'Business',
                        type: 'select',
                        options: this.businessList.map(b => ({ label: b.name, value: b.id, departments: b.departments })),
                        placeholder: 'Select Business'
                    },
                    {
                        key: 'department_id',
                        label: 'Department',
                        type: 'select',
                        options: [], // Will be populated based on business selection
                        placeholder: 'Select Department'
                    }
                ],
                currentFilters: {
                    name: this.currentFilterState.name,
                    business_id: this.currentFilterState.business_id,
                    department_id: this.currentFilterState.department_id
                }
            };

            // Open dialog
            const dialogRef = this.dialog.open(FilterDialogComponent, {
                width: '600px',
                data: filterData
            });

            // Listen for dialog events and pass business list for dependent dropdown
            dialogRef.componentInstance.data = filterData;
            dialogRef.componentInstance.businessList = this.businessList;

            dialogRef.afterClosed().subscribe(result => {
                if (result !== undefined) {
                    console.log('Applied filters:', result);
                    this.applyFilters(result);
                }
            });
        });
    }

    // Apply filters
    applyFilters(filters: any): void {
        this.currentFilterState = {
            page: 1,
            name: filters.name || '',
            business_id: filters.business_id || '',
            department_id: filters.department_id || ''
        };
        this.currentPage = 1;
        this.loadDepartmentList();
    }

    // Pagination methods
    onPageChanged(page: number): void {
        this.currentPage = page;
        this.currentFilterState.page = page;
        this.loadDepartmentList();
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.onPageChanged(this.currentPage - 1);
        }
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.onPageChanged(this.currentPage + 1);
        }
    }

    firstPage(): void {
        this.onPageChanged(1);
    }

    lastPage(): void {
        this.onPageChanged(this.totalPages);
    }

    getNumericButtons(): number[] {
        const buttons = [];
        const start = Math.max(1, this.currentPage - 2);
        const end = Math.min(this.totalPages, this.currentPage + 2);
        
        for (let i = start; i <= end; i++) {
            buttons.push(i);
        }
        return buttons;
    }

    gotoPage(page: number): void {
        this.onPageChanged(page);
    }

    // Add department dialog
    addDepartment(): void {
        const dialogRef = this.dialog.open(DepartmentDialogComponent, {
            width: '700px',
            data: { 
                type: 'create'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                console.log('New department data:', result);
                this.saveDepartment(result);
            }
        });
    }

    // Save new department
    saveDepartment(departmentData: any): void {
        console.log('Saving department:', departmentData);
        // Implement API call here
        // After successful save, reload the list
        // this.loadDepartmentList();
    }

    // Edit department
    editDept(dept: any): void {
        console.log(dept, "-------------------------------dept");
        this.loaderService.setLoading(true);
        let business;
        // Support multiple shapes: dept.business may be an object or a string
        if (dept && dept.business) {
            if (typeof dept.business === 'object') {
                business = dept.business.id;
            } else {
                for (let i = 0; i < this.businessList.length; i++) {
                    if (dept.business == this.businessList[i].name || dept.business_name == this.businessList[i].name) {
                        business = this.businessList[i].id;
                        break;
                    }
                }
            }
        } else {
            for (let i = 0; i < this.businessList.length; i++) {
                if (dept.business == this.businessList[i].name || dept.business_name == this.businessList[i].name) {
                    business = this.businessList[i].id;
                    break;
                }
            }
        }
        
        let dialogRef = this.dialog.open(DepartmentDialogComponent, {
            data: {
                type: 'edit',
                data: {
                    "id": dept.id,
                    "head": dept.head,
                    "name": dept.name,
                    "abbr": dept.abbr,
                    "business": business
                }
            },
        });
        
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadDepartmentList();
            }
            this.loaderService.setLoading(false);
        });
    }

    // Export departments using new API
    exportDepartments(): void {
        this.loaderService.setLoading(true);
        const exportParams = {
            name: this.currentFilterState.name,
            business_id: this.currentFilterState.business_id,
            department_id: this.currentFilterState.department_id
        };

        console.log('Department Export API params:', exportParams);

        this.hrservice.getDepartmentListExport(exportParams).subscribe(
            (data: any) => {
                const blob = new Blob([data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `departments_${this.getExportDate()}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                this.loaderService.setLoading(false);
            },
            (error) => {
                console.error('Export error:', error);
                alert('Export failed. Please try again.');
                this.loaderService.setLoading(false);
            }
        );
    }

    private getExportDate(): string {
        return new Date().toISOString().slice(0, 10);
    }
}